import React, { useEffect, useRef, useState } from 'react';

interface CountUpProps {
    end: number;
    decimals?: number;
    duration?: number;
    className?: string;
}

// Counts from 0 to `end` once the number scrolls into view
const CountUp = ({ end, decimals = 0, duration = 1500, className }: CountUpProps) => {
    const ref = useRef<HTMLSpanElement>(null);
    const [value, setValue] = useState(0);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        if (
            window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
            !('IntersectionObserver' in window)
        ) {
            setValue(end);
            return;
        }

        let frame = 0;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                observer.disconnect();
                const start = performance.now();
                const tick = (now: number) => {
                    const progress = Math.min((now - start) / duration, 1);
                    const eased = 1 - Math.pow(1 - progress, 3);
                    setValue(end * eased);
                    if (progress < 1) frame = requestAnimationFrame(tick);
                };
                frame = requestAnimationFrame(tick);
            },
            { threshold: 0.5 }
        );
        observer.observe(element);

        return () => {
            observer.disconnect();
            cancelAnimationFrame(frame);
        };
    }, [end, duration]);

    return (
        <span ref={ref} className={className} aria-label={end.toFixed(decimals)}>
            {value.toFixed(decimals)}
        </span>
    );
};

export default CountUp;
